// A disclosure: the button carries aria-expanded and aria-controls, and the panel stays in the DOM (hidden when
// closed), so the aria-controls target always exists. Controlled (open + onOpenChange) or uncontrolled (defaultOpen).

import { useCallback, useState } from "react";
import { useSvgId } from "./useSvgId";

export interface DisclosureOptions {
  open?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  /** A caller-chosen panel id; default a generated one. */
  id?: string | undefined;
}

export interface Disclosure {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  button: { "aria-expanded": boolean; "aria-controls": string; onClick: () => void };
  panel: { id: string; hidden: boolean };
}

export function useDisclosure({ open: controlled, defaultOpen = false, onOpenChange, id }: DisclosureOptions = {}): Disclosure {
  const generated = useSvgId();
  const panelId = id ?? `${generated}-panel`;
  const [own, setOwn] = useState(defaultOpen);
  const open = controlled ?? own;
  const setOpen = useCallback((next: boolean) => {
    if (controlled === undefined) setOwn(next);
    onOpenChange?.(next);
  }, [controlled, onOpenChange]);
  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);
  return {
    open,
    setOpen,
    toggle,
    button: { "aria-expanded": open, "aria-controls": panelId, onClick: toggle },
    panel: { id: panelId, hidden: !open },
  };
}
